import React, { useEffect, useRef } from "react";

/**
 * AiEvolutionInfographicAnimation
 * High-precision SVG motion graphics overlay calibrated directly to the 1030x529 hero.png artwork.
 * 
 * Features:
 * 1. Primary Timeline Energy Pulse: Smooth, continuous light pulse traveling from Past -> Present -> Future -> Arrowhead.
 * 2. Dynamic Timeline Markers: Past (271,400), Present (475,390), Future (735,333) with synchronized glow brightening as pulse passes.
 * 3. Isometric Platform Edge Glows: Subtly oscillating neon edge illumination across all 5 stages.
 * 4. Automation Gear Aura: Gentle cyan breathing glow centered on the gears.
 * 5. Cognitive Enterprise City Shimmer: Vertical upward light sweep along the central skyscraper spire.
 * 6. Responsive & Lightweight: SVG viewBox matching image dimensions, GPU accelerated, zero layout shifts.
 */

// Timeline path calibrated to the visible curved trajectory
const TIMELINE_PATH_D =
  "M 240,404 C 330,402 410,396 475,390 C 560,382 660,362 735,333 C 780,314 820,295 852,283";

// Marker locations along the timeline
const MARKER_PAST = { x: 271, y: 400 };
const MARKER_PRESENT = { x: 475, y: 390 };
const MARKER_FUTURE = { x: 735, y: 333 };
const ARROW_ENDPOINT = { x: 852, y: 283 };

// Timing in seconds
const TRAVEL_DURATION = 5.2; // 5.2s travel
const REST_DURATION = 0.8;   // 0.8s pause & fade before restart
const TOTAL_CYCLE = TRAVEL_DURATION + REST_DURATION; // 6.0s total

export const AiEvolutionInfographicAnimation = () => {
  const pathRef = useRef(null);
  const pulseGroupRef = useRef(null);
  const trailRef1 = useRef(null);
  const trailRef2 = useRef(null);
  const trailRef3 = useRef(null);

  const markerPastGlowRef = useRef(null);
  const markerPresentGlowRef = useRef(null);
  const markerFutureGlowRef = useRef(null);

  useEffect(() => {
    // Respect user's reduced motion settings
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const pathEl = pathRef.current;
    if (!pathEl) return;

    const totalLength = pathEl.getTotalLength();
    if (!totalLength) return;

    // Calculate normalized progress of markers along the path
    // We can compute length to each marker
    let presentLength = 0;
    let futureLength = 0;
    
    // Sample along path to find length closest to markers
    for (let l = 0; l <= totalLength; l += 2) {
      const pt = pathEl.getPointAtLength(l);
      if (Math.hypot(pt.x - MARKER_PRESENT.x, pt.y - MARKER_PRESENT.y) < 6 && !presentLength) {
        presentLength = l;
      }
      if (Math.hypot(pt.x - MARKER_FUTURE.x, pt.y - MARKER_FUTURE.y) < 6 && !futureLength) {
        futureLength = l;
      }
    }
    const presentProgress = presentLength ? presentLength / totalLength : 0.45;
    const futureProgress = futureLength ? futureLength / totalLength : 0.83;

    let animId;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = ((timestamp - startTime) / 1000) % TOTAL_CYCLE;

      // Pulse position & opacity
      if (elapsed <= TRAVEL_DURATION) {
        // Normal travel
        const progress = elapsed / TRAVEL_DURATION;
        const currentDist = progress * totalLength;
        const headPt = pathEl.getPointAtLength(currentDist);

        if (pulseGroupRef.current) {
          pulseGroupRef.current.setAttribute(
            "transform",
            `translate(${headPt.x}, ${headPt.y})`
          );

          // Fade-in at start (0 -> 0.06), full (0.06 -> 0.90), fade-out at arrowhead (0.90 -> 1.0)
          let opacity = 1.0;
          if (progress < 0.06) {
            opacity = progress / 0.06;
          } else if (progress > 0.90) {
            opacity = Math.max(0, (1.0 - progress) / 0.10);
          }
          pulseGroupRef.current.style.opacity = opacity.toString();
        }

        // Comet tail particles (spaced slightly behind)
        const updateTrail = (ref, offsetDist, baseOpacity) => {
          if (!ref.current) return;
          const trailDist = Math.max(0, currentDist - offsetDist);
          const tPt = pathEl.getPointAtLength(trailDist);
          ref.current.setAttribute("transform", `translate(${tPt.x}, ${tPt.y})`);
          let op = baseOpacity;
          if (progress < 0.08) op *= progress / 0.08;
          else if (progress > 0.90) op *= (1.0 - progress) / 0.10;
          ref.current.style.opacity = op.toString();
        };

        updateTrail(trailRef1, 10, 0.7);
        updateTrail(trailRef2, 22, 0.45);
        updateTrail(trailRef3, 36, 0.22);

        // Marker reaction when pulse passes
        // Present marker boost when progress is near presentProgress
        const presentDistFromPulse = Math.abs(progress - presentProgress);
        if (markerPresentGlowRef.current) {
          if (presentDistFromPulse < 0.06) {
            const boost = 1.0 - presentDistFromPulse / 0.06;
            markerPresentGlowRef.current.style.opacity = (0.3 + boost * 0.7).toString();
            markerPresentGlowRef.current.style.transform = `scale(${1 + boost * 0.35})`;
          } else {
            markerPresentGlowRef.current.style.opacity = "0.25";
            markerPresentGlowRef.current.style.transform = "scale(1)";
          }
        }

        // Future marker boost when progress is near futureProgress
        const futureDistFromPulse = Math.abs(progress - futureProgress);
        if (markerFutureGlowRef.current) {
          if (futureDistFromPulse < 0.06) {
            const boost = 1.0 - futureDistFromPulse / 0.06;
            markerFutureGlowRef.current.style.opacity = (0.3 + boost * 0.7).toString();
            markerFutureGlowRef.current.style.transform = `scale(${1 + boost * 0.35})`;
          } else {
            markerFutureGlowRef.current.style.opacity = "0.25";
            markerFutureGlowRef.current.style.transform = "scale(1)";
          }
        }
      } else {
        // Rest period before looping
        if (pulseGroupRef.current) pulseGroupRef.current.style.opacity = "0";
        if (trailRef1.current) trailRef1.current.style.opacity = "0";
        if (trailRef2.current) trailRef2.current.style.opacity = "0";
        if (trailRef3.current) trailRef3.current.style.opacity = "0";
        if (markerPresentGlowRef.current) {
          markerPresentGlowRef.current.style.opacity = "0.25";
          markerPresentGlowRef.current.style.transform = "scale(1)";
        }
        if (markerFutureGlowRef.current) {
          markerFutureGlowRef.current.style.opacity = "0.25";
          markerFutureGlowRef.current.style.transform = "scale(1)";
        }
      }

      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <svg
      viewBox="0 0 1030 529"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0 h-full w-full pointer-events-none select-none overflow-visible"
    >
      <defs>
        {/* Glow Filters */}
        <filter id="cyanGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="strongNeonGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="blur1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur2" />
          <feMerge>
            <feMergeNode in="blur1" />
            <feMergeNode in="blur2" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="cityBeamGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
        </filter>

        {/* Pulse Gradients */}
        <radialGradient id="pulseCoreGrad">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="35%" stopColor="#80F5FF" stopOpacity="0.9" />
          <stop offset="70%" stopColor="#00D2EE" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0066FF" stopOpacity="0" />
        </radialGradient>

        <radialGradient id="cyanAmbientGrad">
          <stop offset="0%" stopColor="#00D2EE" stopOpacity="0.8" />
          <stop offset="50%" stopColor="#0077FF" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#0044AA" stopOpacity="0" />
        </radialGradient>

        <radialGradient id="purpleAmbientGrad">
          <stop offset="0%" stopColor="#C084FC" stopOpacity="0.7" />
          <stop offset="50%" stopColor="#818CF8" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#4F46E5" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Embedded CSS Keyframes for Subtle Ambient Platform & City Lighting */}
      <style>{`
        @keyframes platformEdgeBreathe {
          0%, 100% { opacity: 0.22; transform: scale(0.98); }
          50% { opacity: 0.55; transform: scale(1.02); }
        }
        @keyframes gearAuraBreathe {
          0%, 100% { opacity: 0.20; transform: scale(0.95); }
          50% { opacity: 0.60; transform: scale(1.05); }
        }
        @keyframes citySpirePulse {
          0%, 100% { opacity: 0.30; transform: scaleY(0.95); }
          50% { opacity: 0.75; transform: scaleY(1.04); }
        }
        @keyframes cityBeaconTwinkle {
          0%, 100% { opacity: 0.40; r: 2.5px; }
          50% { opacity: 0.95; r: 4px; }
        }
        @keyframes serverDataShimmer {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.45; }
        }
        @keyframes markerSubtleIdle {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50% { opacity: 0.60; transform: scale(1.2); }
        }

        .anim-platform-1 { animation: platformEdgeBreathe 4.2s ease-in-out infinite; transform-origin: 312px 345px; }
        .anim-platform-2 { animation: platformEdgeBreathe 3.8s ease-in-out infinite 0.7s; transform-origin: 438px 305px; }
        .anim-platform-3 { animation: platformEdgeBreathe 4.5s ease-in-out infinite 1.4s; transform-origin: 570px 275px; }
        .anim-platform-4 { animation: platformEdgeBreathe 3.6s ease-in-out infinite 2.1s; transform-origin: 700px 245px; }
        .anim-platform-5 { animation: platformEdgeBreathe 3.2s ease-in-out infinite 0.3s; transform-origin: 865px 230px; }

        .anim-gear-aura { animation: gearAuraBreathe 4.0s ease-in-out infinite; transform-origin: 438px 285px; }
        .anim-city-spire { animation: citySpirePulse 3.4s ease-in-out infinite; transform-origin: 865px 200px; }
        .anim-city-beacon { animation: cityBeaconTwinkle 2.2s ease-in-out infinite; }
        .anim-server-shimmer { animation: serverDataShimmer 3.5s ease-in-out infinite; }
        .anim-marker-idle { animation: markerSubtleIdle 2.6s ease-in-out infinite; }
      `}</style>

      {/* =========================================================================
          1. INVISIBLE REFERENCE PATH (Follows exact visible curved timeline)
         ========================================================================= */}
      <path
        ref={pathRef}
        d={TIMELINE_PATH_D}
        fill="none"
        stroke="transparent"
        strokeWidth="1"
      />

      {/* =========================================================================
          2. PLATFORM EDGE ILLUMINATION ACCENTS (Subtle, localized neon breathing)
         ========================================================================= */}
      {/* Platform 1 (Software): Translucent server stack base */}
      <g className="anim-platform-1">
        <ellipse
          cx="312"
          cy="345"
          rx="52"
          ry="22"
          fill="url(#purpleAmbientGrad)"
          style={{ mixBlendMode: "screen" }}
        />
        <path
          d="M 264 345 L 312 364 L 360 345"
          stroke="#A855F7"
          strokeWidth="1.5"
          strokeOpacity="0.4"
          fill="none"
          filter="url(#cyanGlow)"
        />
      </g>

      {/* Platform 1 Server stack edge lines shimmer */}
      <g className="anim-server-shimmer">
        <line x1="284" y1="318" x2="330" y2="336" stroke="#C084FC" strokeWidth="1" strokeOpacity="0.35" />
        <line x1="284" y1="332" x2="330" y2="350" stroke="#818CF8" strokeWidth="1" strokeOpacity="0.35" />
      </g>

      {/* Platform 2 (Automation): Neon cyan border glow */}
      <g className="anim-platform-2">
        <ellipse
          cx="438"
          cy="305"
          rx="56"
          ry="24"
          fill="url(#cyanAmbientGrad)"
          style={{ mixBlendMode: "screen" }}
        />
        <path
          d="M 386 305 L 438 326 L 490 305"
          stroke="#00D2EE"
          strokeWidth="1.5"
          strokeOpacity="0.5"
          fill="none"
          filter="url(#cyanGlow)"
        />
      </g>

      {/* Platform 2 Gears Subtle Cyan Aura */}
      <g className="anim-gear-aura">
        <circle
          cx="438"
          cy="285"
          r="26"
          fill="url(#cyanAmbientGrad)"
          style={{ mixBlendMode: "screen" }}
          filter="url(#cyanGlow)"
        />
      </g>

      {/* Platform 3 (AI Agents): Humanoid robot platform */}
      <g className="anim-platform-3">
        <ellipse
          cx="570"
          cy="275"
          rx="58"
          ry="25"
          fill="url(#cyanAmbientGrad)"
          style={{ mixBlendMode: "screen" }}
        />
        <path
          d="M 516 275 L 570 297 L 624 275"
          stroke="#38BDF8"
          strokeWidth="1.5"
          strokeOpacity="0.45"
          fill="none"
          filter="url(#cyanGlow)"
        />
      </g>

      {/* Platform 3 Robot Eye / Visor Soft Glint */}
      <circle
        cx="570"
        cy="242"
        r="4.5"
        fill="#00D2EE"
        opacity="0.35"
        className="anim-marker-idle"
        filter="url(#cyanGlow)"
      />

      {/* Platform 4 (Digital Workers): Collaboration platform */}
      <g className="anim-platform-4">
        <ellipse
          cx="700"
          cy="245"
          rx="66"
          ry="28"
          fill="url(#cyanAmbientGrad)"
          style={{ mixBlendMode: "screen" }}
        />
        <path
          d="M 640 245 L 700 269 L 760 245"
          stroke="#00D2EE"
          strokeWidth="1.5"
          strokeOpacity="0.45"
          fill="none"
          filter="url(#cyanGlow)"
        />
      </g>

      {/* Platform 5 (Cognitive Enterprise): Smart City & Skyscraper complex */}
      <g className="anim-platform-5">
        <ellipse
          cx="865"
          cy="230"
          rx="82"
          ry="34"
          fill="url(#cyanAmbientGrad)"
          style={{ mixBlendMode: "screen" }}
        />
        <path
          d="M 788 230 L 865 260 L 942 230"
          stroke="#00F5FF"
          strokeWidth="2"
          strokeOpacity="0.6"
          fill="none"
          filter="url(#strongNeonGlow)"
        />
      </g>

      {/* Platform 5 City Skyscraper Center Spire Upward Light Shimmer */}
      <g className="anim-city-spire">
        <line
          x1="865"
          y1="130"
          x2="865"
          y2="210"
          stroke="#00F5FF"
          strokeWidth="3"
          strokeOpacity="0.45"
          strokeLinecap="round"
          filter="url(#cityBeamGlow)"
          style={{ mixBlendMode: "screen" }}
        />
      </g>

      {/* Platform 5 Skyscraper Top Beacon Pulse */}
      <circle
        cx="865"
        cy="126"
        r="3"
        fill="#FFFFFF"
        filter="url(#cyanGlow)"
        className="anim-city-beacon"
      />

      {/* =========================================================================
          3. SECONDARY ANIMATION: TIMELINE MARKERS GLOW ACCENTS
         ========================================================================= */}
      {/* Marker 1: Past (271, 400) */}
      <g transform={`translate(${MARKER_PAST.x}, ${MARKER_PAST.y})`}>
        <circle
          ref={markerPastGlowRef}
          r="8"
          fill="none"
          stroke="#A855F7"
          strokeWidth="1.5"
          opacity="0.35"
          className="anim-marker-idle"
          filter="url(#cyanGlow)"
        />
      </g>

      {/* Marker 2: Present (475, 390) */}
      <g transform={`translate(${MARKER_PRESENT.x}, ${MARKER_PRESENT.y})`}>
        <circle
          ref={markerPresentGlowRef}
          r="9"
          fill="none"
          stroke="#00D2EE"
          strokeWidth="2"
          opacity="0.25"
          filter="url(#strongNeonGlow)"
          style={{ transformOrigin: "0px 0px", transition: "opacity 0.2s ease-out, transform 0.2s ease-out" }}
        />
        <circle r="4" fill="#00D2EE" opacity="0.3" filter="url(#cyanGlow)" />
      </g>

      {/* Marker 3: Future (735, 333) */}
      <g transform={`translate(${MARKER_FUTURE.x}, ${MARKER_FUTURE.y})`}>
        <circle
          ref={markerFutureGlowRef}
          r="9"
          fill="none"
          stroke="#00D2EE"
          strokeWidth="2"
          opacity="0.25"
          filter="url(#strongNeonGlow)"
          style={{ transformOrigin: "0px 0px", transition: "opacity 0.2s ease-out, transform 0.2s ease-out" }}
        />
        <circle r="4" fill="#00D2EE" opacity="0.3" filter="url(#cyanGlow)" />
      </g>

      {/* =========================================================================
          4. PRIMARY ANIMATION: TRAVELING TIMELINE ENERGY PULSE + COMET TAIL
         ========================================================================= */}
      {/* Trailing Comet Particles (rendered underneath head) */}
      <g ref={trailRef3} opacity="0">
        <circle r="2" fill="#00BFFF" opacity="0.4" filter="url(#cyanGlow)" />
      </g>
      <g ref={trailRef2} opacity="0">
        <circle r="3" fill="#00D2EE" opacity="0.6" filter="url(#cyanGlow)" />
      </g>
      <g ref={trailRef1} opacity="0">
        <circle r="4.5" fill="#38BDF8" opacity="0.75" filter="url(#cyanGlow)" />
      </g>

      {/* Main Traveling Energy Pulse Head */}
      <g ref={pulseGroupRef} opacity="0">
        {/* Wide ambient outer aura */}
        <circle r="16" fill="url(#cyanAmbientGrad)" opacity="0.45" filter="url(#cyanGlow)" />
        {/* Mid intense cyan halo */}
        <circle r="8" fill="#00D2EE" opacity="0.85" filter="url(#strongNeonGlow)" />
        {/* Inner bright white core */}
        <circle r="3.5" fill="#FFFFFF" />
      </g>
    </svg>
  );
};

export default AiEvolutionInfographicAnimation;

